"use client"

import {
    ColumnDef,
    flexRender,
    functionalUpdate,
    getCoreRowModel,
    getSortedRowModel,
    OnChangeFn,
    SortingState,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import {
    ContextMenuTrigger,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenu,
    ContextMenuSeparator,
    ContextMenuLabel,
} from "@/components/ui/context-menu"
import { cn } from "@/lib/utils"
import { useEffect, useState, useRef } from "react"
import type { MouseEvent as ReactMouseEvent } from "react"
import { Trash2, Eye, Edit3, ListChecks, Copy, Archive, ArchiveRestore, UserPlus, UserMinus, Loader2, User, ExternalLink, ScrollText, Database, UserCog } from "lucide-react"
import { resolveItemCreatorUid, uidsEqual } from "./resolveItemOwnerUid"
import { useDispatch, useSelector, useStore } from "react-redux"
import { useLocation, useParams } from "react-router-dom"
import JSONPretty from "../JSONpretty"
import { ActionTypes } from "@/actions"
import { Button } from "@mui/material"
import { Badge } from "@/components/ui/badge"
import { TooltipProvider } from "@/components/ui/tooltip"
import type { ActionDefinition } from "../api/actionsApi"
import { buildRowFieldSummary } from "./buildRowFieldSummary"
import { isItemArchived } from "@/utils/itemArchive"
import { isFormSubmissionRow } from "../utils/formSubmissionRowUtils"
import type { ItemPermissionAction } from "../acl/buildItemPermissionBulkRequests"
import type { ItemPermissionFlags } from "../acl/itemMenuPermissions"
import { resolveTableColumnWidth } from "./componentTable/tableColumnSizing"

const EMPTY_SORTING: SortingState = []

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[],
    onDoubleClick: any,
    onDetails: any,
    onDuplicate?: (item: TData) => void,
    onArchive?: (item: TData) => void,
    onRestore?: (item: TData) => void,
    onDelete: any,
    onSorted: any,
    /** Redux table slug for persisting sort (defaults to route :slug param). */
    sortTableKey?: string,
    page?: number,
    pageSize?: number,
    selectedRowsCount?: number,
    getItemPermissionFlags?: (uid?: string) => ItemPermissionFlags,
    isItemPermissionPending?: (action: ItemPermissionAction) => boolean,
    canDeleteSelected?: boolean,
    onRowClick?: (item: TData, event: ReactMouseEvent<HTMLElement>) => void,
    actionMode?: boolean,
    actionAppliedUid?: string | null,
    actionMenuActions?: ActionDefinition[],
    /** When set, context menu in action mode includes "Apply" for the toolbar-selected action */
    selectedToolbarAction?: ActionDefinition | null,
    onApplyToolbarAction?: (item: TData) => void,
    onRunAction?: (action: ActionDefinition, item: TData) => void,
    actionInProgress?: { uid: string; field: string } | { uids: string[]; field: string } | null,
    highlightUid?: string | null,
    /** When non-empty, each row gets an aria-label built from these field names */
    rowSummaryFieldNames?: string[],
    columnVisibility?: VisibilityState,
    onColumnVisibilityChange?: OnChangeFn<VisibilityState>,
    getItemDelegateActionsVisible?: (item: TData) => boolean,
    onDelegateOwner?: (item: TData) => void,
    onRemoveDelegatedOwner?: (item: TData) => void,
    rowHasDelegatedOwner?: (item: TData) => boolean,
    /** When true, archived rows are visually de-emphasized (e.g. “show all” mode). */
    dimArchivedRows?: boolean,
    emptyTableTitle?: string,
    emptyTableDescription?: string,
    /** When set, that column's header stacks above other sticky headers so its filter panel is not covered. */
    activeFilterColumnId?: string | null,
    onFormSubmissionShowUser?: (item: TData) => void,
    onFormSubmissionShowForm?: (item: TData) => void,
    onFormSubmissionShowLogs?: () => void,
    onFormSubmissionImpersonateUser?: (item: TData) => void,
    impersonatingSubmissionUid?: string | null,
    currentUserUid?: string | null,
    /** Admin: open Mongo inspector for this row’s collection + uid. */
    onShowInMongo?: (item: TData) => void,
    /** When false, rows are already server-paged; do not slice again in the UI. */
    paginateRows?: boolean,
    /** When true, sorting is applied on the server; parent refetches on sort change. */
    manualSorting?: boolean,
    /** Sticky header offset when a toolbar sits above the table in the same scroll container. */
    stickyHeaderTopPx?: number,
}

export function DataTable<TData, TValue>({
    columns,
    data,
    onDoubleClick,
    onDetails,
    onDuplicate,
    onArchive,
    onRestore,
    onDelete,
    onSorted,
    sortTableKey,
    page,
    pageSize,
    selectedRowsCount = 0,
    getItemPermissionFlags,
    isItemPermissionPending = () => false,
    canDeleteSelected = false,
    onRowClick,
    actionMode = false,
    actionAppliedUid = null,
    actionMenuActions = [],
    selectedToolbarAction = null,
    onApplyToolbarAction,
    onRunAction,
    actionInProgress = null,
    highlightUid = null,
    rowSummaryFieldNames = [],
    columnVisibility: columnVisibilityProp,
    onColumnVisibilityChange: onColumnVisibilityChangeProp,
    getItemDelegateActionsVisible,
    onDelegateOwner,
    onRemoveDelegatedOwner,
    rowHasDelegatedOwner,
    dimArchivedRows = false,
    emptyTableTitle,
    emptyTableDescription,
    activeFilterColumnId = null,
    onFormSubmissionShowUser,
    onFormSubmissionShowForm,
    onFormSubmissionShowLogs,
    onFormSubmissionImpersonateUser,
    impersonatingSubmissionUid = null,
    currentUserUid = null,
    onShowInMongo,
    paginateRows = true,
    manualSorting = false,
    stickyHeaderTopPx = 0,
}: DataTableProps<TData, TValue>) {
    const { slug } = useParams();
    const routeSlug = slug || '';
    const sortStorageSlug = sortTableKey || routeSlug;
    const dispatch = useDispatch();

    // Get sorts from Redux
    const sortFromStore = useSelector((state: any) => {
        const tableData = state.table.tables.find((t: any) => t.slug === sortStorageSlug);
        return tableData?.sorting ?? EMPTY_SORTING;
    });

    // Initialize local state with sortFromStore
    const [localSorting, setLocalSorting] = useState<SortingState>(sortFromStore);
    const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>({});
    const lastUserSortingRef = useRef<SortingState | null>(null);
    const isInitialMount = useRef(true);

    const columnVisibility = columnVisibilityProp ?? internalColumnVisibility;
    const onColumnVisibilityChange: OnChangeFn<VisibilityState> =
        onColumnVisibilityChangeProp ?? setInternalColumnVisibility;

    // Initialize from Redux on mount or when sortFromStore first becomes available
    useEffect(() => {
        if (isInitialMount.current) {
            if (sortFromStore && sortFromStore.length > 0) {
                setLocalSorting(sortFromStore);
            }
            isInitialMount.current = false;
        }
    }, [sortFromStore]);

    // Sync Redux state to local state when it changes (but not when it's from our own user action)
    useEffect(() => {
        if (!isInitialMount.current && sortFromStore) {
            const sortFromStoreStr = JSON.stringify(sortFromStore);
            const lastUserSortingStr = lastUserSortingRef.current ? JSON.stringify(lastUserSortingRef.current) : null;
            const localSortingStr = JSON.stringify(localSorting);

            // Only sync if:
            // 1. Redux state is different from local state, AND
            // 2. This change didn't come from a user action (or the ref was cleared)
            if (sortFromStoreStr !== localSortingStr && sortFromStoreStr !== lastUserSortingStr) {
                setLocalSorting(sortFromStore);
            }
            // Clear the ref after processing
            lastUserSortingRef.current = null;
        }
    }, [sortFromStore, localSorting]);

    // Create the table with localSorting
    const table = useReactTable({
        data,
        columns: columns ?? [],
        getCoreRowModel: getCoreRowModel(),
        ...(manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
        manualSorting,
        onSortingChange: (updater) => {
            // Update local state
            const newSorting = updater instanceof Function ? updater(localSorting) : updater;
            setLocalSorting(newSorting);

            // Store this as the last user action to prevent sync loop
            lastUserSortingRef.current = Array.isArray(newSorting)
                ? [...newSorting]
                : newSorting;

            // Create a clean copy for Redux
            const sortingCopy = Array.isArray(newSorting)
                ? [...newSorting]
                : newSorting;

            // Update Redux store
            dispatch({
                type: ActionTypes.TABLE_SET_SORTING,
                payload: { slug: sortStorageSlug, sorting: sortingCopy }
            });

            // Notify parent so it can reset to page 1 (show first page of newly sorted data)
            onSorted?.(sortingCopy);
        },
        onColumnVisibilityChange,
        state: {
            sorting: localSorting,
            columnVisibility,
        },
    });

    const allRows = table.getRowModel().rows;
    const pagedRows =
        paginateRows &&
            typeof page === 'number' &&
            typeof pageSize === 'number'
            ? allRows.slice((page - 1) * pageSize, page * pageSize)
            : allRows;

    useEffect(() => {
        if (!highlightUid) return;
        const el = document.querySelector<HTMLElement>(`[data-row-uid="${highlightUid}"]`);
        if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [highlightUid, pagedRows.length]);

    const tableMinWidth = table.getHeaderGroups()[0]?.headers.reduce(
        (sum, header) => sum + resolveTableColumnWidth(header.column),
        0
    ) ?? 0;

    return (
        <TooltipProvider delayDuration={400}>
            <div className="w-full min-w-0">
                <Table
                    className="w-full table-fixed"
                    style={tableMinWidth > 0 ? { minWidth: tableMinWidth } : undefined}
                >
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                                {headerGroup.headers.map((header) => {
                                    const columnWidth = resolveTableColumnWidth(header.column);
                                    return (
                                        <TableHead
                                            key={header.id}
                                            className={cn(
                                                "sticky overflow-hidden bg-gray-50 px-4 py-4 text-left align-middle text-xs font-semibold uppercase tracking-wider text-gray-700 shadow-[0_1px_0_0_rgb(229_231_235)] dark:bg-gray-900 dark:text-gray-300 dark:shadow-[0_1px_0_0_rgb(55_65_81)]",
                                                activeFilterColumnId === header.column.id ? "z-30" : "z-20"
                                            )}
                                            style={{
                                                top: stickyHeaderTopPx,
                                                width: `${columnWidth}px`,
                                                minWidth: `${columnWidth}px`,
                                                maxWidth: `${columnWidth}px`,
                                            }}
                                        >
                                            <div className="w-full min-w-0 overflow-hidden">
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </div>
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {pagedRows?.length ? (
                            pagedRows.map((row, index) => {
                                const rowUid = (row.original as any)?.uid as string | undefined;
                                const rowOriginal = row.original as any;
                                const permissions = rowUid ? getItemPermissionFlags?.(rowUid) : undefined;
                                const readPending = isItemPermissionPending("read");
                                const updatePending = isItemPermissionPending("update");
                                const deletePending = isItemPermissionPending("delete");
                                const delegatePending = isItemPermissionPending("delegate");
                                const canRead = !readPending && Boolean(permissions?.canRead);
                                const canUpdate = !updatePending && Boolean(permissions?.canUpdate);
                                const canDelete = !deletePending && Boolean(permissions?.canDelete);
                                const deleteAllowed = selectedRowsCount > 1 ? canDeleteSelected : canDelete;
                                const canArchiveRow =
                                    Boolean(onArchive) &&
                                    canUpdate &&
                                    !isItemArchived(rowOriginal as Record<string, unknown>);
                                const canRestoreRow =
                                    Boolean(onRestore) &&
                                    canUpdate &&
                                    isItemArchived(rowOriginal as Record<string, unknown>);
                                const rowIsArchived = isItemArchived(rowOriginal as Record<string, unknown>);
                                const isHighlighted = Boolean(highlightUid && rowUid && rowUid === highlightUid);
                                const shouldFlash = Boolean(actionAppliedUid && rowUid && rowUid === actionAppliedUid);
                                const rowAriaLabel =
                                    rowSummaryFieldNames.length > 0 && rowOriginal
                                        ? buildRowFieldSummary(
                                            rowOriginal as Record<string, unknown>,
                                            rowSummaryFieldNames
                                        )
                                        : undefined;
                                const hasUserActions = Array.isArray(actionMenuActions) && actionMenuActions.length > 0;
                                const delegateVisible =
                                    getItemDelegateActionsVisible?.(row.original as TData) ?? false;
                                const showDelegateContext = Boolean(
                                    !delegatePending &&
                                    delegateVisible &&
                                    onDelegateOwner &&
                                    selectedRowsCount <= 1
                                );
                                const showRemoveDelegatedContext = Boolean(
                                    !delegatePending &&
                                    delegateVisible &&
                                    onRemoveDelegatedOwner &&
                                    rowHasDelegatedOwner?.(row.original as TData) &&
                                    selectedRowsCount <= 1
                                );

                                return (
                                    <ContextMenu key={row.id}>
                                        <ContextMenuTrigger asChild>
                                            <TableRow
                                                key={row.id}
                                                aria-label={rowAriaLabel || undefined}
                                                data-state={row.getIsSelected() && "selected"}
                                                data-highlight={isHighlighted ? "true" : undefined}
                                                data-row-uid={rowUid || undefined}
                                                onClick={(event) => {
                                                    if (actionMode && onRowClick) {
                                                        onRowClick(row.original, event);
                                                    }
                                                }}
                                                onDoubleClick={() => {
                                                    if (!actionMode && canUpdate && !rowIsArchived) {
                                                        onDoubleClick(row.original);
                                                    }
                                                }}
                                                className={`
                                                transition-colors duration-1000 ease-out
                                                hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-gray-700 dark:hover:to-gray-600
                                                hover:shadow-sm cursor-pointer
                                                ${row.getIsSelected() ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 dark:border-blue-400' : ''}
                                                ${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/30 dark:bg-gray-800/50'}
                                                ${shouldFlash ? 'border-y-2 border-blue-400/60 bg-blue-50/70 dark:bg-blue-900/20' : ''}
                                                ${isHighlighted ? 'bg-amber-50 dark:bg-amber-900/40 border-l-4 border-amber-400 dark:border-amber-300' : ''}
                                                ${dimArchivedRows && rowIsArchived ? 'opacity-60 text-gray-600 dark:text-gray-400' : ''}
                                                border-b border-gray-100 dark:border-gray-700 last:border-b-0
                                            `}
                                            >
                                                {row.getVisibleCells().map((cell) => {
                                                    const isTimestampColumn = cell.column.id === '_created' || cell.column.id === '_updated';
                                                    const columnWidth = resolveTableColumnWidth(cell.column);
                                                    const progressUids =
                                                        actionInProgress && 'uids' in actionInProgress
                                                            ? actionInProgress.uids
                                                            : actionInProgress && 'uid' in actionInProgress
                                                                ? [actionInProgress.uid]
                                                                : [];
                                                    const isActionCellLoading =
                                                        Boolean(
                                                            actionInProgress &&
                                                            rowUid &&
                                                            progressUids.includes(rowUid) &&
                                                            actionInProgress.field === cell.column.id
                                                        );
                                                    return (
                                                        <TableCell
                                                            key={cell.id}
                                                            className={`overflow-hidden px-6 py-4 text-sm text-gray-900 dark:text-gray-100 ${isTimestampColumn ? "" : "whitespace-nowrap"}`}
                                                            style={{ width: `${columnWidth}px`, minWidth: `${columnWidth}px`, maxWidth: `${columnWidth}px` }}
                                                        >
                                                            <div className={isTimestampColumn ? "min-w-0 overflow-hidden" : "flex min-w-0 items-center space-x-2 overflow-hidden"}>
                                                                {isActionCellLoading && (
                                                                    <Loader2 className="h-4 w-4 shrink-0 text-indigo-500 animate-spin" aria-hidden />
                                                                )}
                                                                <div className={`min-w-0 flex-1 overflow-hidden ${isActionCellLoading ? "opacity-70" : ""}`}>
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        </ContextMenuTrigger>
                                        <ContextMenuContent
                                            key={`context-${row.id}`}
                                            className="w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-lg p-1"
                                        >
                                            {actionMode ? (
                                                <>
                                                    {selectedToolbarAction && onApplyToolbarAction && (
                                                        <>
                                                            <ContextMenuLabel className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                                                Selected action
                                                            </ContextMenuLabel>
                                                            <ContextMenuItem
                                                                onClick={() => onApplyToolbarAction(row.original)}
                                                                className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/30 hover:text-emerald-900 dark:hover:text-emerald-200 cursor-pointer"
                                                            >
                                                                <ListChecks className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                                                <span>Apply: {selectedToolbarAction.name}</span>
                                                            </ContextMenuItem>
                                                            <ContextMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />
                                                        </>
                                                    )}
                                                    <ContextMenuLabel className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                                        Other actions
                                                    </ContextMenuLabel>
                                                    {actionMenuActions.length > 0 ? (
                                                        actionMenuActions.map((action) => (
                                                            <ContextMenuItem
                                                                key={action.uid || action.name}
                                                                onClick={() => {
                                                                    onRunAction?.(action, row.original)
                                                                }}
                                                                className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-900 dark:hover:text-indigo-300 cursor-pointer"
                                                            >
                                                                <ListChecks className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                                                <span>{action.name}</span>
                                                            </ContextMenuItem>
                                                        ))
                                                    ) : (
                                                        <ContextMenuItem
                                                            disabled={true}
                                                            className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 rounded-md"
                                                        >
                                                            {selectedToolbarAction ? 'No other actions' : 'No actions'}
                                                        </ContextMenuItem>
                                                    )}
                                                    {(showDelegateContext || showRemoveDelegatedContext) && (
                                                        <>
                                                            <ContextMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />
                                                            <ContextMenuLabel className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                                                Item owner
                                                            </ContextMenuLabel>
                                                            {showDelegateContext && (
                                                                <ContextMenuItem
                                                                    onClick={() =>
                                                                        onDelegateOwner?.(row.original as TData)
                                                                    }
                                                                    className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-violet-50 dark:hover:bg-violet-900/30 hover:text-violet-900 dark:hover:text-violet-200 cursor-pointer"
                                                                >
                                                                    <UserPlus className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                                                                    <span>Delegate owner</span>
                                                                </ContextMenuItem>
                                                            )}
                                                            {showRemoveDelegatedContext && (
                                                                <ContextMenuItem
                                                                    onClick={() =>
                                                                        onRemoveDelegatedOwner?.(row.original as TData)
                                                                    }
                                                                    className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-slate-100 cursor-pointer"
                                                                >
                                                                    <UserMinus className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                                                    <span>Remove delegated owner</span>
                                                                </ContextMenuItem>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {canRead ? (
                                                        <ContextMenuItem
                                                            onClick={() => onDetails(row.original)}
                                                            className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-900 dark:hover:text-blue-300 cursor-pointer"
                                                        >
                                                            <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                            <span>View Details</span>
                                                        </ContextMenuItem>
                                                    ) : null}

                                                    {selectedRowsCount <= 1 &&
                                                        isFormSubmissionRow(row.original as Record<string, unknown>) &&
                                                        ((onFormSubmissionShowUser &&
                                                            onFormSubmissionShowForm &&
                                                            onFormSubmissionShowLogs) ||
                                                            onFormSubmissionImpersonateUser) && (
                                                            <>
                                                                <ContextMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />
                                                                <ContextMenuLabel className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                                                    Form submission
                                                                </ContextMenuLabel>
                                                                {onFormSubmissionImpersonateUser && (() => {
                                                                    const rowRecord = row.original as Record<string, unknown>
                                                                    const submitterUid = resolveItemCreatorUid(rowRecord)
                                                                    const isOwnUser = uidsEqual(submitterUid, currentUserUid)
                                                                    const isImpersonating = Boolean(
                                                                        submitterUid &&
                                                                        impersonatingSubmissionUid === submitterUid
                                                                    )
                                                                    return (
                                                                        <ContextMenuItem
                                                                            onClick={() =>
                                                                                onFormSubmissionImpersonateUser(row.original as TData)
                                                                            }
                                                                            disabled={
                                                                                !submitterUid ||
                                                                                isOwnUser ||
                                                                                impersonatingSubmissionUid !== null
                                                                            }
                                                                            className={`flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors ${!submitterUid || isOwnUser || impersonatingSubmissionUid !== null ? 'opacity-50 cursor-not-allowed' : 'hover:bg-violet-50 dark:hover:bg-violet-900/30 hover:text-violet-900 dark:hover:text-violet-200 cursor-pointer'}`}
                                                                        >
                                                                            <UserCog className="w-4 h-4 text-violet-700 dark:text-violet-400" />
                                                                            <span>
                                                                                {isImpersonating ? 'Impersonating...' : 'Impersonate user'}
                                                                            </span>
                                                                        </ContextMenuItem>
                                                                    )
                                                                })()}
                                                                {onFormSubmissionShowUser ? (
                                                                    <ContextMenuItem
                                                                        onClick={() =>
                                                                            onFormSubmissionShowUser(row.original as TData)
                                                                        }
                                                                        className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-cyan-50 dark:hover:bg-cyan-900/30 hover:text-cyan-900 dark:hover:text-cyan-200 cursor-pointer"
                                                                    >
                                                                        <User className="w-4 h-4 text-cyan-700 dark:text-cyan-400" />
                                                                        <span>Show user</span>
                                                                    </ContextMenuItem>
                                                                ) : null}
                                                                {onFormSubmissionShowForm ? (
                                                                    <ContextMenuItem
                                                                        onClick={() =>
                                                                            onFormSubmissionShowForm(row.original as TData)
                                                                        }
                                                                        className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-900 dark:hover:text-indigo-200 cursor-pointer"
                                                                    >
                                                                        <ExternalLink className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                                                        <span>Show form</span>
                                                                    </ContextMenuItem>
                                                                ) : null}
                                                                {onFormSubmissionShowLogs ? (
                                                                    <ContextMenuItem
                                                                        onClick={() => onFormSubmissionShowLogs()}
                                                                        className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-stone-100 dark:hover:bg-stone-800/80 hover:text-stone-900 dark:hover:text-stone-100 cursor-pointer"
                                                                    >
                                                                        <ScrollText className="w-4 h-4 text-stone-700 dark:text-stone-400" />
                                                                        <span>Show logs</span>
                                                                    </ContextMenuItem>
                                                                ) : null}
                                                            </>
                                                        )}

                                                    {selectedRowsCount <= 1 &&
                                                        onShowInMongo &&
                                                        rowUid && (
                                                            <>
                                                                <ContextMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />
                                                                <ContextMenuLabel className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                                                    Admin
                                                                </ContextMenuLabel>
                                                                <ContextMenuItem
                                                                    onClick={() => onShowInMongo(row.original as TData)}
                                                                    className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:text-amber-950 dark:hover:text-amber-100 cursor-pointer"
                                                                >
                                                                    <Database className="w-4 h-4 text-amber-800 dark:text-amber-400" />
                                                                    <span>Show in Mongo</span>
                                                                </ContextMenuItem>
                                                            </>
                                                        )}

                                                    {canUpdate && !rowIsArchived ? (
                                                        <ContextMenuItem
                                                            onClick={() => onDoubleClick(row.original)}
                                                            className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-green-50 dark:hover:bg-green-900/30 hover:text-green-900 dark:hover:text-green-300 cursor-pointer"
                                                        >
                                                            <Edit3 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                                            <span>Edit {selectedRowsCount > 1 ? 'Items' : 'Item'}</span>
                                                        </ContextMenuItem>
                                                    ) : null}

                                                    {showDelegateContext && (
                                                        <ContextMenuItem
                                                            onClick={() =>
                                                                onDelegateOwner?.(row.original as TData)
                                                            }
                                                            className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-violet-50 dark:hover:bg-violet-900/30 hover:text-violet-900 dark:hover:text-violet-200 cursor-pointer"
                                                        >
                                                            <UserPlus className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                                                            <span>Delegate owner</span>
                                                        </ContextMenuItem>
                                                    )}

                                                    {showRemoveDelegatedContext && (
                                                        <ContextMenuItem
                                                            onClick={() =>
                                                                onRemoveDelegatedOwner?.(row.original as TData)
                                                            }
                                                            className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-slate-100 cursor-pointer"
                                                        >
                                                            <UserMinus className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                                            <span>Remove delegated owner</span>
                                                        </ContextMenuItem>
                                                    )}

                                                    <ContextMenuItem
                                                        onClick={() => {
                                                            onDuplicate?.(row.original as TData);
                                                        }}
                                                        disabled={!onDuplicate}
                                                        className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-900 dark:hover:text-purple-300 cursor-pointer"
                                                    >
                                                        <Copy className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                                        <span>Duplicate</span>
                                                    </ContextMenuItem>

                                                    {(rowIsArchived ? onRestore : onArchive) ? (
                                                        <>
                                                            <ContextMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />
                                                            {rowIsArchived ? (
                                                                <ContextMenuItem
                                                                    onClick={() => onRestore?.(row.original as TData)}
                                                                    disabled={!canRestoreRow}
                                                                    className={`flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors ${!canRestoreRow ? 'opacity-50 cursor-not-allowed' : 'hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-900 dark:hover:text-teal-300 cursor-pointer'}`}
                                                                >
                                                                    <ArchiveRestore className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                                                                    <span>Restore</span>
                                                                </ContextMenuItem>
                                                            ) : (
                                                                <ContextMenuItem
                                                                    onClick={() => onArchive?.(row.original as TData)}
                                                                    disabled={!canArchiveRow}
                                                                    className={`flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors ${!canArchiveRow ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-50 dark:hover:bg-orange-900/30 hover:text-orange-900 dark:hover:text-orange-300 cursor-pointer'}`}
                                                                >
                                                                    <Archive className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                                                    <span>Archive</span>
                                                                </ContextMenuItem>
                                                            )}
                                                        </>
                                                    ) : null}

                                                    {deleteAllowed ? (
                                                        <>
                                                            <ContextMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />

                                                            <ContextMenuItem
                                                                onClick={() => onDelete(row.original)}
                                                                className="flex items-center space-x-3 px-3 py-2 text-sm text-red-700 dark:text-red-400 rounded-md transition-colors hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-900 dark:hover:text-red-300 cursor-pointer"
                                                            >
                                                                <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                                                                <span>Delete {selectedRowsCount > 1 ? 'Items' : 'Item'}</span>
                                                            </ContextMenuItem>
                                                        </>
                                                    ) : null}

                                                    {hasUserActions && canUpdate && (
                                                        <>
                                                            <ContextMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />
                                                            <ContextMenuLabel className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                                                User actions
                                                            </ContextMenuLabel>
                                                            {actionMenuActions.map((action) => (
                                                                <ContextMenuItem
                                                                    key={action.uid || action.name}
                                                                    onClick={() => {
                                                                        onRunAction?.(action, row.original as TData);
                                                                    }}
                                                                    className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-900 dark:hover:text-indigo-300 cursor-pointer"
                                                                >
                                                                    <ListChecks className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                                                    <span>{action.name}</span>
                                                                </ContextMenuItem>
                                                            ))}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </ContextMenuContent>
                                    </ContextMenu>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-32 text-center"
                                >
                                    <div className="flex flex-col items-center justify-center space-y-3">
                                        <svg className="w-12 h-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                        </svg>
                                        <div className="text-center">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
                                                {emptyTableTitle ?? "No data available"}
                                            </h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {emptyTableDescription ??
                                                    "Get started by adding your first item"}
                                            </p>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </TooltipProvider>
    );
}